import React, { useCallback } from 'react';

import { Grid, Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import { getRichTextContent, isRichTextEmpty } from 'utils/richTextUtils';

import { AcAdvancedTextInput } from 'components/AcAdvancedTextInput/AcAdvancedInput';
import AcCard from 'components/AcCard/AcCard';
import Typography from 'components/Typography/Typography';

import { GeneralThemeFormValues } from '../generalTheme.types';

import 'style/forms.scss';
import './MaintenanceSection.scss';

const DEFAULT_MAINTENANCE_TEXT =
  "We're currently performing maintenance. Please check back soon.";

const getCustomMaintenanceText = (text: string): string => {
  const plainText = getRichTextContent(text);
  const isEmptyOrDefault =
    plainText === '' || plainText === DEFAULT_MAINTENANCE_TEXT;
  return isEmptyOrDefault ? '' : plainText;
};

export const MaintenanceSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<GeneralThemeFormValues>();

  const maintenanceTextValue = (() => {
    const raw = values?.maintenanceModeRichText?.text;
    return !raw || isRichTextEmpty(raw) ? DEFAULT_MAINTENANCE_TEXT : raw;
  })();

  const handleMaintenanceTextChange = useCallback(
    (value: string) => {
      if (
        getCustomMaintenanceText(values?.maintenanceModeRichText?.text ?? '') ===
        getCustomMaintenanceText(value)
      )
        return;
      setFieldValue('maintenanceModeRichText.text', value);
    },
    [values?.maintenanceModeRichText?.text, setFieldValue]
  );

  const handleUserContentChange = useCallback(
    (html: string) => {
      setFieldValue('maintenanceModeRichText.text', html);
    },
    [setFieldValue]
  );

  return (
    <div data-testid="GeneralSettings-Maintenance">
      <AcCard stackContainer={false} title="" description="" className="mt-0">
        <Stack gap={0.5} sx={{ marginBottom: 2 }}>
          <Typography tag="h4">Maintenance</Typography>
          <Typography className="text-p-s text-base-mutedForeground">
            Let users know when your project is unavailable.
          </Typography>
        </Stack>
        <Grid container rowSpacing={2} columnSpacing={1.5}>
          <Grid item xs={12}>
            <div className="maintenance-rich-text-14px">
              <AcAdvancedTextInput
                value={maintenanceTextValue}
                setValue={handleMaintenanceTextChange}
                onUserContentChange={handleUserContentChange}
                error={false}
                enableColorPicker={true}
                fontColor={
                  values?.maintenanceModeRichText?.fontColor || 'rgb(0, 0, 0)'
                }
                placeholder="e.g., Write your blog post here. Share exciting updates, game features, behind-the-scenes insights, or special offers."
                customClass="maintenance-rich-text-14px"
              />
            </div>
          </Grid>
        </Grid>
      </AcCard>
    </div>
  );
};
